import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import { useDispatch, useSelector } from "react-redux";
import { setDeletePopup, setRefresh } from "features/alert/alertSlice";
import { deleteAlertConfiguration } from "config/apis";
import { fetchAlertConfigDetails } from "features/alert/alertThunk";

function DeletePopup() {
  const dispatch = useDispatch();
  const deletePopup = useSelector((state) => state.alerts.deletePopup);
  const selectedAlert = useSelector((state) => state.alerts.selectedAlert);

  // ✅ Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleClose = () => {
    dispatch(setDeletePopup(false));
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDelete = async () => {
    if (!selectedAlert) return;

    const token = localStorage.getItem("token");

    const payload = {
      thingIds: [selectedAlert?.thingId || ""],
      alertId: selectedAlert?.alertId,
    };

    try {
      const response = await fetch(deleteAlertConfiguration, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-dhundhoo-session": token,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log("✅ Delete API Response:", result);

      // ✅ Check backend responseCode
      if (result?.responseCode === 200) {
        setSnackbar({
          open: true,
          message: result?.message || "Alert deleted successfully",
          severity: "success",
        });

        dispatch(setRefresh());
        await dispatch(fetchAlertConfigDetails(selectedAlert?.alertId));

        handleClose();
      } else {
        throw new Error(result?.message || "Delete failed");
      }
    } catch (error) {
      console.error("❌ Error deleting alert:", error);

      setSnackbar({
        open: true,
        message: error.message || "Failed to delete alert",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Dialog
        open={deletePopup}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: 6,
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#e53935",
          }}
        >
          <Typography variant="h6" color="white" fontWeight="bold">
            Delete Alert
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Content */}
        <DialogContent sx={{ p: 3 }}>
          <MDBox>
            <Typography variant="body1">
              Are you sure you want to delete this alert?
            </Typography>
            {selectedAlert && (
              <Typography variant="body2" color="text.secondary" mt={1}>
                Alert ID: <b>{selectedAlert.alertId}</b>
              </Typography>
            )}
          </MDBox>
        </DialogContent>

        {/* Actions */}
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <MDButton onClick={handleClose} color="secondary" variant="outlined">
            Cancel
          </MDButton>
          <MDButton onClick={handleDelete} color="error" variant="contained">
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ✅ Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default DeletePopup;
